import { useEffect, useState } from "react";
import StatusPill from "../../components/ui/StatusPill";
import {
  deleteAdminPriceItem,
  fetchAdminJobs,
  fetchAdminOverview,
  fetchAdminPriceItems,
  fetchCalendarEvents,
  saveAdminPriceItem,
  startGoogleCalendarConnect,
} from "../../services/adminApi";

const tabs = ["Prehled", "Zakazky", "Cenik", "Nabidky", "Faktury", "Klienti", "Kalendar"];

const emptyPriceForm = {
  id: "",
  title: "",
  unit: "ks",
  priceFrom: "",
  priceTo: "",
  note: "",
  isVisible: true,
  sortOrder: 0,
};

function AdminDashboard({ activeTab, onTabChange, onLogout }) {
  const [overview, setOverview] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  const [priceForm, setPriceForm] = useState(emptyPriceForm);
  const [status, setStatus] = useState({ type: "loading", message: "Nacitam administraci..." });

  useEffect(() => {
    let mounted = true;

    async function loadAdminData() {
      setStatus({ type: "loading", message: "Nacitam administraci..." });
      try {
        const [overviewData, jobsData, calendarData, pricingData] = await Promise.all([
          fetchAdminOverview(),
          fetchAdminJobs(),
          fetchCalendarEvents(),
          fetchAdminPriceItems(),
        ]);

        if (!mounted) return;
        setOverview(overviewData?.stats || []);
        setJobs(jobsData?.jobs || []);
        setCalendarEvents(calendarData?.events || []);
        setPriceItems(pricingData?.items || []);
        setStatus({ type: "idle", message: "" });
      } catch (error) {
        if (!mounted) return;
        setStatus({ type: "error", message: error.message });
      }
    }

    loadAdminData();
    return () => {
      mounted = false;
    };
  }, []);

  const updatePriceField = (event) => {
    const { name, type, checked, value } = event.target;
    setPriceForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const editPriceItem = (item) => {
    setPriceForm({
      id: item.id,
      title: item.title || "",
      unit: item.unit || "ks",
      priceFrom: item.priceFrom ?? "",
      priceTo: item.priceTo ?? "",
      note: item.note || "",
      isVisible: item.isVisible,
      sortOrder: item.sortOrder ?? 0,
    });
    onTabChange("Cenik");
  };

  const resetPriceForm = () => {
    setPriceForm(emptyPriceForm);
  };

  const submitPriceItem = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukladam polozku ceniku..." });
    try {
      const response = await saveAdminPriceItem(priceForm);
      const saved = response?.item;
      if (!saved) {
        throw new Error("API nevratilo ulozenou polozku.");
      }

      setPriceItems((current) => {
        const exists = current.some((item) => item.id === saved.id);
        const next = exists ? current.map((item) => (item.id === saved.id ? saved : item)) : [...current, saved];
        return next.sort((a, b) => a.sortOrder - b.sortOrder);
      });
      resetPriceForm();
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const removePriceItem = async (id) => {
    setStatus({ type: "loading", message: "Mazu polozku ceniku..." });
    try {
      await deleteAdminPriceItem(id);
      setPriceItems((current) => current.filter((item) => item.id !== id));
      if (priceForm.id === id) {
        resetPriceForm();
      }
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const connectGoogleCalendar = async () => {
    setStatus({ type: "loading", message: "Pripravuji Google Calendar OAuth..." });
    try {
      const response = await startGoogleCalendarConnect();
      if (!response?.authorizationUrl) {
        throw new Error("API nevratilo Google OAuth URL.");
      }
      window.location.href = response.authorizationUrl;
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const renderOverview = () => (
    <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
      <div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {overview.map((item) => (
            <div key={item.label} className="card p-5">
              <div className="text-sm font-medium text-slate-400">{item.label}</div>
              <div className="mt-2 text-3xl font-extrabold text-white">{item.value}</div>
            </div>
          ))}
        </div>
        <div className="mt-6">{renderJobs()}</div>
      </div>
      {renderCalendarCard()}
    </div>
  );

  const renderJobs = () => (
    <div className="card overflow-hidden">
      <div className="border-b border-cyan-300/10 p-5">
        <h2 className="text-lg font-bold text-white">Zakazky</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="bg-cyan-300/5 text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-5 py-3">Zakazka</th>
              <th className="px-5 py-3">Klient</th>
              <th className="px-5 py-3">Stav</th>
              <th className="px-5 py-3">Prubeh</th>
              <th className="px-5 py-3">Rozpocet</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cyan-300/10">
            {jobs.map((job) => (
              <tr key={job.id || job.title}>
                <td className="px-5 py-4 font-medium text-white">{job.title}</td>
                <td className="px-5 py-4 text-slate-300">{job.clientName || job.client}</td>
                <td className="px-5 py-4">
                  <StatusPill>{job.status}</StatusPill>
                </td>
                <td className="px-5 py-4">
                  <div className="h-2 w-32 rounded-full bg-slate-800">
                    <div className="h-2 rounded-full bg-cyan-300" style={{ width: `${job.progressPercent || job.progress || 0}%` }} />
                  </div>
                </td>
                <td className="px-5 py-4 font-medium text-white">{job.budget || job.finalPrice || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!jobs.length && status.type !== "loading" && (
          <p className="p-5 text-sm text-slate-400">API zatim nevratilo zadne zakazky.</p>
        )}
      </div>
    </div>
  );

  const renderPricing = () => (
    <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
      <div className="card overflow-hidden">
        <div className="border-b border-cyan-300/10 p-5">
          <h2 className="text-lg font-bold text-white">Cenik</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-cyan-300/5 text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-5 py-3">Poradi</th>
                <th className="px-5 py-3">Polozka</th>
                <th className="px-5 py-3">Cena</th>
                <th className="px-5 py-3">Viditelna</th>
                <th className="px-5 py-3 text-right">Akce</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cyan-300/10">
              {priceItems.map((item) => (
                <tr key={item.id}>
                  <td className="px-5 py-4 text-slate-300">{item.sortOrder}</td>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-white">{item.title}</div>
                    {item.note && <div className="mt-1 text-xs text-slate-400">{item.note}</div>}
                  </td>
                  <td className="px-5 py-4 font-medium text-white">{item.priceLabel}</td>
                  <td className="px-5 py-4 text-slate-300">{item.isVisible ? "Ano" : "Ne"}</td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <button type="button" className="btn-secondary px-3 py-2" onClick={() => editPriceItem(item)}>
                        Upravit
                      </button>
                      <button type="button" className="btn-secondary px-3 py-2" onClick={() => removePriceItem(item.id)}>
                        Smazat
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!priceItems.length && status.type !== "loading" && (
            <p className="p-5 text-sm text-slate-400">Cenik zatim nema zadne polozky.</p>
          )}
        </div>
      </div>

      <form className="card p-5" onSubmit={submitPriceItem}>
        <h2 className="text-lg font-bold text-white">{priceForm.id ? "Upravit polozku" : "Nova polozka"}</h2>
        <div className="mt-5 grid gap-4">
          <label className="grid gap-1.5 text-sm font-medium text-slate-200">
            Nazev
            <input className="input" name="title" value={priceForm.title} onChange={updatePriceField} required />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5 text-sm font-medium text-slate-200">
              Cena od
              <input className="input" name="priceFrom" type="number" min="0" step="1" value={priceForm.priceFrom} onChange={updatePriceField} />
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-slate-200">
              Cena do
              <input className="input" name="priceTo" type="number" min="0" step="1" value={priceForm.priceTo} onChange={updatePriceField} />
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5 text-sm font-medium text-slate-200">
              Jednotka
              <input className="input" name="unit" value={priceForm.unit} onChange={updatePriceField} required />
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-slate-200">
              Poradi
              <input className="input" name="sortOrder" type="number" step="1" value={priceForm.sortOrder} onChange={updatePriceField} />
            </label>
          </div>
          <label className="grid gap-1.5 text-sm font-medium text-slate-200">
            Poznamka
            <textarea className="textarea" name="note" value={priceForm.note} onChange={updatePriceField} />
          </label>
          <label className="flex items-center gap-3 text-sm font-medium text-slate-200">
            <input name="isVisible" type="checkbox" checked={priceForm.isVisible} onChange={updatePriceField} />
            Zobrazit na webu
          </label>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1">
              Ulozit
            </button>
            <button type="button" className="btn-secondary" onClick={resetPriceForm}>
              Vycistit
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  const renderCalendarCard = () => (
    <aside className="card p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">Google Calendar</h2>
          <p className="mt-1 text-sm text-slate-400">Pouze pro admina.</p>
        </div>
        <button type="button" className="btn-secondary px-3 py-2" onClick={connectGoogleCalendar}>
          Pripojit
        </button>
      </div>
      <div className="mt-5 space-y-3">
        {calendarEvents.map((event) => (
          <div key={event.id || event.title} className="rounded-md border border-cyan-300/15 bg-[#07131d] p-3">
            <div className="text-xs font-semibold text-cyan-300">{event.startsAt || event.time}</div>
            <div className="mt-1 text-sm font-semibold text-white">{event.title}</div>
            <div className="text-sm text-slate-400">{event.location || event.place}</div>
          </div>
        ))}
        {!calendarEvents.length && status.type !== "loading" && (
          <p className="text-sm text-slate-400">API zatim nevratilo zadne udalosti.</p>
        )}
      </div>
    </aside>
  );

  const renderPlaceholder = (title) => (
    <div className="card p-6">
      <h2 className="text-lg font-bold text-white">{title}</h2>
      <p className="mt-3 text-sm leading-6 text-slate-400">
        Tahle sekce je pripravena jako samostatny panel. Data a upravy pro ni muzeme napojit stejnym zpusobem jako cenik.
      </p>
    </div>
  );

  const renderActiveTab = () => {
    if (activeTab === "Prehled") return renderOverview();
    if (activeTab === "Zakazky") return renderJobs();
    if (activeTab === "Cenik") return renderPricing();
    if (activeTab === "Kalendar") return renderCalendarCard();
    return renderPlaceholder(activeTab);
  };

  return (
    <section className="page-section">
      <div className="shell">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="eyebrow">Administrace</p>
            <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-white">Sprava ELTT</h1>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Interni prostredi pro zakazky, klienty, finance a Google Calendar.
            </p>
          </div>
          <button type="button" onClick={onLogout} className="btn-secondary">
            Odhlasit
          </button>
        </div>

        {status.message && (
          <div className={`mt-6 rounded-md border p-4 text-sm ${
            status.type === "error"
              ? "border-red-400/30 bg-red-400/10 text-red-200"
              : "border-cyan-300/20 bg-cyan-300/10 text-cyan-100"
          }`}
          >
            {status.message}
          </div>
        )}

        <div className="mt-8 overflow-x-auto">
          <div className="flex min-w-max gap-2">
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => onTabChange(tab)}
                className={`rounded-md px-3 py-2 text-sm font-semibold ${
                  activeTab === tab ? "bg-cyan-300 text-slate-950" : "bg-cyan-300/5 text-slate-300 hover:bg-cyan-300/10"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {renderActiveTab()}
        </div>
      </div>
    </section>
  );
}

export default AdminDashboard;
